import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Link, useNavigate } from 'react-router-dom';
import Register from './Register';
import BackHomeLink from './BackHomeLink';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const joinCode = urlParams.get('join_code');
    if (joinCode) {
      setJoinMessage('You have a lab invitation! Please sign in or create an account to accept.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username.trim(), password);

    if (!result.success) {
      // Lockout nudge (C15). The lockout message says "wait 30 minutes" and
      // nothing else — but the usual cause is a forgotten password, and
      // resetting it is both faster than waiting and the thing they actually
      // need. The old inline link lived at the bottom of the form, which is
      // not where someone reads after being refused; as a toast action it
      // sits right next to the refusal.
      if (/lock/i.test(result.error)) {
        toast.withAction(
          result.error,
          { label: 'Reset password', onAction: () => navigate('/forgot-password') },
          { type: 'error' }
        );
      } else {
        toast.error(result.error);
      }
    }

    setLoading(false);
  };

  if (showRegister) {
    return <Register onBack={() => setShowRegister(false)} />;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <BackHomeLink />
        <div className="auth-logo">SqueakBase</div>
        <p className="auth-subtitle">
          Welcome back! Please sign in to your account.
        </p>

        <form onSubmit={handleSubmit} className="auth-form">
          {joinMessage && <div className="success" style={{ backgroundColor: '#e7f3ff', color: '#0c5460' }}>{joinMessage}</div>}

          <div className="form-group">
            <label htmlFor="username">
              Username or Email <span className="required">*</span>
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username or email"
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password <span className="required">*</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button"
          >
            {loading ? (
              <span className="auth-loading">Signing in...</span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button
          type="button"
          onClick={() => setShowRegister(true)}
          className="auth-button secondary"
        >
          Create New Account
        </button>

        <div className="auth-footer">
          <p>
            <Link to="/forgot-password" className="auth-link">
              Forgot your password?
            </Link>
          </p>
          <p className="field-hint">
            First time here? Create an account to get started with your lab management.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
